import React, { useState, useEffect, useRef } from "react";
import {
  getEmployeeList,
  fetchSkillMatrix,
  getAllSkills,
} from "../../../../api/ApiConfig";
import {
  FaSearch,
  FaTimes,
  FaUserTie,
  FaBuilding,
  FaEnvelope,
  FaUserFriends,
  FaLayerGroup,
  FaCheckCircle,
  FaCode,
  FaDatabase,
  FaServer,
  FaMobileAlt,
  FaCloud,
  FaTools,
  FaPhoneAlt,
  FaArrowLeft,
  FaMapMarkerAlt,
  FaStar,
} from "react-icons/fa";
import { MdVerified, MdWorkspacePremium, MdArrowForward } from "react-icons/md";
import "./SkillMatrix.css";

// --- Helpers ---
const getInitials = (name) => {
  if (!name) return "??";
  const parts = name.trim().split(" ");
  return parts.length === 1
    ? parts[0].charAt(0).toUpperCase()
    : (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getLevelStyles = (level, years) => {
  const lvl = level?.toLowerCase() || "";
  const y = parseInt(years || 0, 10);
  let type = "novice";
  if (lvl.includes("expert") || lvl.includes("advanced") || y >= 5)
    type = "expert";
  else if (lvl.includes("intermediate") || y >= 2) type = "intermediate";
  else type = "beginner";

  const styles = {
    expert: {
      color: "#059669",
      gradient: "linear-gradient(135deg, #d1fae5 0%, #10b981 100%)",
      label: "Expert",
    },
    intermediate: {
      color: "#2563eb",
      gradient: "linear-gradient(135deg, #dbeafe 0%, #3b82f6 100%)",
      label: "Intermediate",
    },
    beginner: {
      color: "#d97706",
      gradient: "linear-gradient(135deg, #fef3c7 0%, #f59e0b 100%)",
      label: "Beginner",
    },
    novice: {
      color: "#4b5563",
      gradient: "linear-gradient(135deg, #f3f4f6 0%, #9ca3af 100%)",
      label: "Novice",
    },
  };
  return styles[type] || styles.novice;
};

const getSkillIcon = (category, name) => {
  const txt = (category + " " + name).toLowerCase();
  if (txt.includes("database") || txt.includes("sql")) return <FaDatabase />;
  if (
    txt.includes("frontend") ||
    txt.includes("react") ||
    txt.includes("angular")
  )
    return <FaCode />;
  if (txt.includes("backend") || txt.includes("node") || txt.includes("java"))
    return <FaServer />;
  if (txt.includes("mobile") || txt.includes("android")) return <FaMobileAlt />;
  if (txt.includes("cloud") || txt.includes("aws")) return <FaCloud />;
  return <FaTools />;
};

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime())
    ? null
    : date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

// --- Components ---

// 1. Profile Banner
const ProfileHeader = ({ employee, skillCount, certCount, onBack }) => (
  <div className="profile-banner">
    <div className="banner-bg-accent"></div>
    {onBack && (
      <button className="back-btn-skill" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>
    )}
    <div className="banner-content">
      <div className="profile-main">
        <div className="avatar-wrapper">
          <div className="avatar-circle">
            {getInitials(employee.employee_name)}
          </div>
          <div className="avatar-badge">
            <MdVerified />
          </div>
        </div>
        <div className="profile-details">
          <h1 className="emp-name-skill">{employee.employee_name}</h1>
          <div className="emp-badges">
            <span className="glass-pill">
              <FaUserTie /> {employee.designation || "N/A"}
            </span>
            <span className="glass-pill">
              <FaBuilding /> {employee.department || "N/A"}
            </span>
          </div>
          <div className="contact-row">
            {employee.employee_email && (
              <a
                href={`mailto:${employee.employee_email}`}
                className="contact-link"
              >
                <FaEnvelope /> {employee.employee_email}
              </a>
            )}
            {employee.employee_phone && (
              <span className="contact-span">
                <FaPhoneAlt /> {employee.employee_phone}
              </span>
            )}
          </div>
        </div>
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-icon-bg">
              <FaLayerGroup />
            </div>
            <div className="stat-info">
              <span className="stat-value">{skillCount}</span>
              <span className="stat-label-sm">Skills</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon-bg">
              <MdWorkspacePremium />
            </div>
            <div className="stat-info">
              <span className="stat-value">{certCount}</span>
              <span className="stat-label-sm">Certs</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// 2. Skill Tile
const SkillTile = ({ skill, index }) => {
  const styleConfig = getLevelStyles(
    skill.skill_level,
    skill.total_experience_years,
  );
  const subSkills = skill.sub_skills
    ? typeof skill.sub_skills === "string"
      ? skill.sub_skills.split(",")
      : skill.sub_skills
    : [];

  const isPrimary = skill.is_primary === "Yes";

  return (
    <div
      className="skill-tile fade-in-up"
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {isPrimary && (
        <div className="primary-star">
          <FaStar />
        </div>
      )}
      <div
        className="tile-top-accent"
        style={{ background: styleConfig.gradient }}
      ></div>
      <div className="tile-header">
        <div className="icon-box" style={{ color: styleConfig.color }}>
          {getSkillIcon(skill.skill_category || "", skill.skill_name || "")}
        </div>
        <div
          className="level-tag"
          style={{ color: styleConfig.color, borderColor: styleConfig.color }}
        >
          {styleConfig.label}
        </div>
      </div>
      <h3 className="tile-title">{skill.skill_name}</h3>
      <p className="tile-subtitle">
        {skill.total_experience_years || 0} Years Experience
      </p>
      {subSkills.length > 0 && (
        <div className="chips-wrapper">
          {subSkills.slice(0, 4).map((sub, idx) => (
            <span key={idx} className="mini-chip">
              {sub.trim()}
            </span>
          ))}
          {subSkills.length > 4 && (
            <span className="mini-chip">+{subSkills.length - 4}</span>
          )}
        </div>
      )}
    </div>
  );
};

// 3. Employee Result Card
const EmployeeResultCard = ({ data, onClick, index }) => {
  const emp = data.EmployeeInformation;
  const skills = data.SkillInformation || [];
  const highlightSkill = skills[0] || {};

  return (
    <div
      className="emp-result-card fade-in-up"
      onClick={onClick}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="card-header-row">
        <div className="card-avatar-lg">{getInitials(emp.employee_name)}</div>
        <div className="card-identity">
          <h3 className="card-name">{emp.employee_name}</h3>
          <span className="card-role">{emp.designation}</span>
        </div>
      </div>

      <div className="card-stats-grid">
        <div className="stat-item">
          <span className="stat-label">Department</span>
          <span className="stat-val">{emp.department || "N/A"}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Manager</span>
          <span className="stat-val">
            {emp.reporting_manager ? emp.reporting_manager : "N/A"}
          </span>
        </div>
      </div>

      {highlightSkill.skill_name && (
        <div className="skill-match-box">
          <span className="match-label">Matched Skill</span>
          <div className="match-value">
            <FaCheckCircle />
            <span>
              {highlightSkill.skill_name} (
              {highlightSkill.total_experience_years || 0} Yrs)
            </span>
          </div>
        </div>
      )}

      <div className="card-hover-action">
        <MdArrowForward />
      </div>
    </div>
  );
};

// --- Main Application ---
const SkillMatrix = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [masterSkills, setMasterSkills] = useState([]);

  const [viewMode, setViewMode] = useState("hero");
  const [skillResultList, setSkillResultList] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const [loadingSearch, setLoadingSearch] = useState(false);
  const [activeTab, setActiveTab] = useState("primary");

  const searchWrapperRef = useRef(null);

  // Initial Fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empRes, skillRes] = await Promise.all([
          getEmployeeList(),
          getAllSkills(),
        ]);
        setEmployees(empRes || []);
        setMasterSkills(
          Array.isArray(skillRes) ? skillRes : skillRes?.data || [],
        );
      } catch (e) {
        console.error("Init Error:", e);
      }
    };
    fetchData();
  }, []);

  // Filter Logic
  const [filteredOptions, setFilteredOptions] = useState([]);
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions([]);
      setIsDropdownOpen(false);
      return;
    }
    const term = searchTerm.toLowerCase();
    if (searchType === "name") {
      const results = employees.filter(
        (emp) =>
          (emp.Employee_Name || "").toLowerCase().includes(term) ||
          (emp.Employee_ID || "").toLowerCase().includes(term),
      );
      setFilteredOptions(results.slice(0, 10));
    } else {
      const results = masterSkills.filter((skill) =>
        skill?.toLowerCase().includes(term),
      );
      setFilteredOptions(results.slice(0, 10));
    }
  }, [searchTerm, searchType, employees, masterSkills]);

  // Click Outside
  useEffect(() => {
    const handleClick = (e) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target)
      )
        setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Handlers
  const handleClearSearch = () => {
    setSearchTerm("");
    setViewMode("hero");
    setSkillResultList([]);
    setSelectedProfile(null);
    setIsDropdownOpen(false);
  };

  const handleSelectPerson = async (empId, name) => {
    setSearchTerm(name);
    setIsDropdownOpen(false);
    setLoadingSearch(true);
    try {
      const res = await fetchSkillMatrix({ emp_id: empId });
      if (res.success === "true" && res.data) {
        const dataObj = Array.isArray(res.data)
          ? Array.isArray(res.data[0])
            ? res.data[0][0]
            : res.data[0]
          : res.data;
        setSelectedProfile(dataObj);
        setViewMode("profile");
        setActiveTab("primary");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSelectSkill = async (skillName) => {
    setSearchTerm(skillName);
    setIsDropdownOpen(false);
    setLoadingSearch(true);

    try {
      const res = await fetchSkillMatrix({ skill_name: skillName });

      if (res.success === "true" && res.data) {
        // 🔥 FIX: flatten nested array
        const employees = Array.isArray(res.data[0]) ? res.data[0] : res.data;

        setSkillResultList(employees);
        setViewMode("skill_list");
      } else {
        setSkillResultList([]);
        setViewMode("skill_list");
      }
    } catch (e) {
      console.error(e);
      setSkillResultList([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  // --- Profile Data Preparation ---
  const allSkills = selectedProfile?.SkillInformation || [];
  const primarySkills = allSkills.filter((s) => s.is_primary === "Yes");
  const secondarySkills = allSkills.filter((s) => s.is_primary !== "Yes");
  const certifications = selectedProfile?.Certifications || [];

  return (
    <div className="app-container">
      <div className="bg-gradient-mesh"></div>

      <nav className="glass-nav">
        <div className="nav-content">
          <div className="brand-logo" onClick={handleClearSearch}>
            <div className="logo-icon">
              <FaLayerGroup />
            </div>
            <span>
              Skill<span className="text-highlight"> Finder</span>
            </span>
          </div>

          <div className="search-widget" ref={searchWrapperRef}>
            <div
              className={`search-bar ${isDropdownOpen ? "active-focus" : ""}`}
            >
              <div className="filter-toggle">
                <button
                  className={searchType === "name" ? "active" : ""}
                  onClick={() => {
                    setSearchType("name");
                    handleClearSearch();
                  }}
                >
                  People
                </button>
                <button
                  className={searchType === "skill" ? "active" : ""}
                  onClick={() => {
                    setSearchType("skill");
                    handleClearSearch();
                  }}
                >
                  Skills
                </button>
                <div
                  className="toggle-slider"
                  style={{
                    transform:
                      searchType === "name"
                        ? "translateX(0)"
                        : "translateX(100%)",
                  }}
                ></div>
              </div>

              <input
                type="text"
                placeholder={
                  searchType === "name"
                    ? "Search by Name..."
                    : "Search by Skill..."
                }
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsDropdownOpen(true); // 👈 open only when typing
                }}
                onFocus={() => {
                  if (searchTerm) setIsDropdownOpen(true);
                }}
              />

              <div className="icon-zone">
                {searchTerm ? (
                  <FaTimes
                    className="action-icon clear"
                    onClick={handleClearSearch}
                  />
                ) : (
                  <FaSearch className="action-icon search" />
                )}
              </div>
            </div>

            {isDropdownOpen && (
              <div className="results-dropdown">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((item, idx) =>
                    searchType === "skill" ? (
                      <div
                        key={idx}
                        className="result-row"
                        onClick={() => handleSelectSkill(item)}
                      >
                        <div className="row-avatar">
                          <FaTools />
                        </div>
                        <div className="row-info">
                          <div className="row-name">{item}</div>
                        </div>
                      </div>
                    ) : (
                      <div
                        key={item.Employee_ID}
                        className="result-row"
                        onClick={() =>
                          handleSelectPerson(
                            item.Employee_ID,
                            item.Employee_Name,
                          )
                        }
                      >
                        <div className="row-avatar">
                          {getInitials(item.Employee_Name)}
                        </div>
                        <div className="row-info">
                          <div className="row-name">{item.Employee_Name}</div>
                          <div className="row-role">
                            {item.Employee_Designation}
                          </div>
                        </div>
                      </div>
                    ),
                  )
                ) : (
                  <div
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    No matches found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="main-viewport">
        {loadingSearch ? (
          <div className="center-loader">
            <div className="spinner-large"></div>
            <p style={{ marginTop: "20px", color: "#64748b" }}>Searching...</p>
          </div>
        ) : (
          <div className="scroll-area">
            {viewMode === "hero" && (
              <div className="landing-hero fade-in-up">
                <div className="hero-graphic">
                  <FaCloud />
                </div>
                <h1 className="text-highlight">Find Your Experts</h1>
                <p>
                  Search specifically by technical skills to find the right
                  resource, or lookup employee profiles directly.
                </p>
              </div>
            )}

            {viewMode === "skill_list" && (
              <div className="skill-results-container">
                <div className="results-header">
                  <h2>
                    Results for{" "}
                    <span className="text-highlight">"{searchTerm}"</span>
                  </h2>
                  <span className="count-badge">
                    {skillResultList.length} Found
                  </span>
                </div>

                <div className="cards-grid">
                  {skillResultList.map((data, i) => (
                    <EmployeeResultCard
                      key={i}
                      data={data}
                      index={i}
                      onClick={() => {
                        setSelectedProfile(data);
                        setViewMode("profile");
                        setActiveTab("primary");
                      }}
                    />
                  ))}
                </div>
                {skillResultList.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#64748b",
                    }}
                  >
                    No employees found with this skill.
                  </div>
                )}
              </div>
            )}

            {viewMode === "profile" && selectedProfile && (
              <div className="profile-wrapper fade-in-up">
                <ProfileHeader
                  employee={selectedProfile.EmployeeInformation}
                  skillCount={allSkills.length}
                  certCount={certifications.length}
                  onBack={
                    skillResultList.length > 0
                      ? () => setViewMode("skill_list")
                      : null
                  }
                />

                <div className="tabs-nav">
                  <button
                    className={`nav-tab ${activeTab === "primary" ? "active" : ""}`}
                    onClick={() => setActiveTab("primary")}
                  >
                    Primary Skills ({primarySkills.length})
                  </button>
                  <button
                    className={`nav-tab ${activeTab === "secondary" ? "active" : ""}`}
                    onClick={() => setActiveTab("secondary")}
                  >
                    Secondary Skills ({secondarySkills.length})
                  </button>
                  <button
                    className={`nav-tab ${activeTab === "certs" ? "active" : ""}`}
                    onClick={() => setActiveTab("certs")}
                  >
                    Certifications ({certifications.length})
                  </button>
                </div>

                <div className="content-area">
                  {/* PRIMARY SKILLS TAB */}
                  {activeTab === "primary" && (
                    <div className="cards-grid skills-grid">
                      {primarySkills.length > 0 ? (
                        primarySkills.map((skill, i) => (
                          <SkillTile key={i} skill={skill} index={i} />
                        ))
                      ) : (
                        <div
                          style={{
                            gridColumn: "1/-1",
                            textAlign: "center",
                            color: "#64748b",
                            padding: "20px",
                          }}
                        >
                          No primary skills listed.
                        </div>
                      )}
                    </div>
                  )}

                  {/* SECONDARY SKILLS TAB */}
                  {activeTab === "secondary" && (
                    <div className="cards-grid skills-grid">
                      {secondarySkills.length > 0 ? (
                        secondarySkills.map((skill, i) => (
                          <SkillTile key={i} skill={skill} index={i} />
                        ))
                      ) : (
                        <div
                          style={{
                            gridColumn: "1/-1",
                            textAlign: "center",
                            color: "#64748b",
                            padding: "20px",
                          }}
                        >
                          No secondary skills listed.
                        </div>
                      )}
                    </div>
                  )}

                  {/* CERTIFICATIONS TAB */}
                  {activeTab === "certs" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "15px",
                      }}
                    >
                      {certifications.length > 0 ? (
                        certifications.map((cert, i) => (
                          <div
                            key={i}
                            className="emp-result-card"
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              padding: "15px 20px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "1.5rem",
                                color: "#10b981",
                                marginRight: "15px",
                              }}
                            >
                              <FaCheckCircle />
                            </div>
                            <div>
                              <h4 style={{ margin: "0 0 5px 0" }}>
                                {cert.certification_title}
                              </h4>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "0.85rem",
                                  color: "#64748b",
                                }}
                              >
                                {cert.issued_by} • {formatDate(cert.issue_date)}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            textAlign: "center",
                            color: "#64748b",
                            padding: "20px",
                          }}
                        >
                          No certifications listed.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SkillMatrix;